import axios from "axios";
import { getToken, setToken, clearToken } from "./tokenStorage";
import { toast } from "../components/ui/Toast";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  withCredentials: true,
});

let refreshRequest = null;
let lastToast = { message: '', time: 0 };

export const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => (
  error?.response?.data?.message || error?.message || fallback
);

const shouldNotifyError = (error) => {
  const method = error?.config?.method?.toUpperCase();
  const url = error?.config?.url || '';
  if (url.includes('/auth/refresh')) return false;
  return method && method !== 'GET';
};

const isPublicAuthRequest = (url = '') => (
  url.includes('/auth/vendor/login') ||
  url.includes('/auth/vendor/signup') ||
  url.includes('/auth/vendor/verify-email') ||
  url.includes('/auth/vendor/resend-verification') ||
  url.includes('/auth/vendor/forgot-password') ||
  url.includes('/auth/vendor/reset-password')
);

const isPublicAuthPage = () => {
  if (typeof window === 'undefined') return false;
  return ['/login', '/signup', '/verify-email', '/forgot-password', '/reset-password'].some((path) => (
    window.location.pathname === path
  ));
};

const notifyError = (message) => {
  const now = Date.now();
  if (lastToast.message === message && now - lastToast.time < 1500) return;
  lastToast = { message, time: now };
  toast.error(message);
};

export const redirectToVendorLogin = (reason = "session_expired") => {
  if (isPublicAuthPage()) return;
  if (window.location.pathname !== "/login") {
    const params = new URLSearchParams();
    params.set("reason", reason);
    params.set("next", `${window.location.pathname}${window.location.search}`);
    window.location.href = `/login?${params.toString()}`;
  }
};

export const refreshVendorSession = async () => {
  refreshRequest = refreshRequest || axiosInstance.post("/auth/refresh");

  try {
    const refreshRes = await refreshRequest;
    const newToken = refreshRes?.data?.data?.accessToken;
    if (!newToken) throw new Error("No access token");
    setToken(newToken);
    return newToken;
  } finally {
    refreshRequest = null;
  }
};

/* Request */
axiosInstance.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* Response */
axiosInstance.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config;
    if (!originalRequest || originalRequest.url?.includes("/auth/refresh")) {
      return Promise.reject(error);
    }
    if (isPublicAuthRequest(originalRequest.url)) {
      return Promise.reject(error);
    }
    if (
      error.response?.status !== 401 ||
      originalRequest._retry
    ) {
      if (shouldNotifyError(error)) {
        notifyError(getApiErrorMessage(error));
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const newToken = await refreshVendorSession();

      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      clearToken("expired");
      if (!isPublicAuthPage()) {
        toast.warning('Your session expired. Please sign in again.');
        redirectToVendorLogin();
      }
      return Promise.reject(refreshError);
    }

    return Promise.reject(error);
  }
);


export default axiosInstance;
